import { Injectable, OnDestroy, inject } from '@angular/core';
import { Location, LocationStrategy } from '@angular/common';
import { ActivatedRoute, Router, UrlTree } from '@angular/router';
import {
  routeBasicNavigation, routeCustomNavigation,
  routeDocumentChanges,
  routeDocumentCopy,
  routeDocumentEdit,
  routeDocumentHistory,
  routeDocumentHistoryView,
  routeDocumentInfo,
  routeDocumentNavigation,
  routeDocumentView,
  routeError,
  routeLogoff,
  routeMessages,
  routeScan,
  routeSearch,
  routeWebDoku
} from '../livedokweb.routes';
import { SelectionType } from '../shared/enums/selection-type';
import { Subscription } from 'rxjs';
import { WebdokuService } from '../web-doku/webdoku.service';
import { SearchOptions } from '../search/search-options';
import { AppUrls } from './app-urls';
import { RBCheck } from '@ldw/roesberg';
import { Base64 } from 'js-base64';

@Injectable({
  providedIn: 'root',
})
export class LdwRouterService implements OnDestroy {
  private webDokuService = inject(WebdokuService);
  private router = inject(Router);
  private location = inject(Location);
  private activatedRoute = inject(ActivatedRoute);
  private locationStrategy = inject(LocationStrategy);

  subscription: Subscription = new Subscription();
  public wasPermaLink = false;

  public openExternal = true;

  constructor() {
    const webDokuService = this.webDokuService;

    // WebDokuService triggers navigation, so we have to use an "event"
    // to start routing
    this.subscription.add(
      webDokuService.navigate.subscribe((data) => {
        this.navigateByUrl(data);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public getUrl() {
    return this.router.url;
  }

  public navigateByUrl(url: string) {
    this.router
      .navigateByUrl(url)
      .then(() => console.log(`navigateByUrl ${url} succeded`))
      .catch((reason) => console.error(`navigateByUrl ${url} failed`, reason));
  }

  public navigateToLoggedOff() {
    this.route(routeLogoff);
  }

  public navigateClose() {
    const url = window.location.origin + '/' + AppUrls.urlCloseWindow;
    window.open(url, '_self');
  }

  public get canNavigateBack(): boolean {
    return this.router.navigated && window.history.length > 1;
  }

  public navigateBack() {
    this.location.back();
  }

  // public navigateToWebDokus() {
  //   if (this.webDokuService.moreThanOneWebDokuAvailable || !this.webDokuService.webDokusAvailable) {
  //     this.route(routeWebDoku);
  //     return;
  //   }
  //   this.navigateToDocuments();
  // }

  public navigateToDocumentsRoot() {
    this.navigateToDocuments(this.webDokuService.currentWebDokuId, '');
  }

  public navigateToDocumentBySelectionType(selectionType: SelectionType, docId: string) {
    if (selectionType == SelectionType.View) {
      this.navigateToDocumentViewer(docId);
    } else if (selectionType == SelectionType.Edit) {
      this.navigateToDocumentEditor(docId);
    } else if (selectionType == SelectionType.History) {
      this.navigateToDocHistory(docId);
    }
  }

  public navigateToDocumentViewer(docId: string, webDokuId?: string, docPage?: string) {
    this.navigateToDocument(routeDocumentView, webDokuId ?? this.webDokuService.currentWebDokuId, docId, docPage);
  }

  public navigateToDocumentEditor(docId: string, docPage?: string) {
    const { page, zoom } = this.activatedRoute.snapshot.queryParams;
    this.navigateToDocument(routeDocumentEdit, this.webDokuService.currentWebDokuId, docId, docPage || page, zoom);
  }

  private navigateToDocument(route: string, webDokuId: string, docId: string, page = '1', zoom = '1') {
    this.router.navigate([route, webDokuId, docId], { queryParams: { page, zoom } });
    /*
    const settings = this.injector.get(SettingsService);

    if (settings.settings.viewMode.openDocInNewTab) {
      const url = AppUrls.getBaseUrlAngular() + route + '/' + webDokuId + '/' + docId;
      window.open(url, '_blank');
      return;
    } else {
      this.router.navigate([route, webDokuId, docId]);
    }

    */
  }

  public getDocumentEditorRoute(docId: string): any[] {
    return ['/', routeDocumentEdit, this.webDokuService.currentWebDokuId, docId];
  }

  public navigateToDocHistory(docId: string) {
    this.router.navigate([routeDocumentHistory, this.webDokuService.currentWebDokuId, docId]);
  }

  public navigateToDocHistoryView(docId: string, archived: string) {
    this.router.navigate([routeDocumentHistoryView, this.webDokuService.currentWebDokuId, docId, { archived }]);
  }

  public getDocHistoryViewRoute(docId: string, archived: string): any[] {
    return ['/', routeDocumentHistoryView, this.webDokuService.currentWebDokuId, docId, { archived }];
  }

  public navigateToDocInfo(docId: string) {
    this.route(routeDocumentInfo, this.getDocumentParams(docId));
  }

  public navigateToDocChanges(docId: string) {
    this.route(routeDocumentChanges, this.getDocumentParams(docId));
  }

  public navigateToDocCopy(docId: string) {
    this.route(routeDocumentCopy, this.getDocumentParams(docId));
  }

  public navigateToDocuments(webDokuId: string, docNodeId: string) {
    if (RBCheck.isNullOrEmpty(docNodeId)) {
      this.route(`${routeDocumentNavigation}/${webDokuId}`);
    } else {
      this.route(`${routeDocumentNavigation}/${webDokuId}`, { nodeId: docNodeId });
    }
  }

  public navigateToDocumentTreeSearch(webDokuId: string, searchText: string) {
    if (RBCheck.isNullOrEmpty(searchText)) {
      this.route(`${routeDocumentNavigation}/${webDokuId}`);
    } else {
      const options = new SearchOptions();
      options.webDokuId = webDokuId;
      options.isTokenSearch = false;
      options.addSearchText(searchText);
      const serialized =this.serializeSearchOptions(options);
      this.router.navigateByUrl(`${routeDocumentNavigation}/${webDokuId}?query=${encodeURIComponent(serialized)}`);
    }
  }

  public navigateToBasic(webDokuId: string, pltNodeId: string) {
    if (RBCheck.isNullOrEmpty(pltNodeId)) {
      this.route(`${routeBasicNavigation}/${webDokuId}`);
    } else {
      this.route(`${routeBasicNavigation}/${webDokuId}`, { nodeId: pltNodeId });
    }
  }

  public navigateToBasicTreeSearch(webDokuId: string, searchText: string) {
    if (RBCheck.isNullOrEmpty(searchText)) {
      this.route(`${routeBasicNavigation}/${webDokuId}`);
    } else {
      const options = new SearchOptions();
      options.webDokuId = webDokuId;
      options.isTokenSearch = false;
      options.addSearchText(searchText);
      const serialized =this.serializeSearchOptions(options);
      this.router.navigateByUrl(`${routeBasicNavigation}/${webDokuId}?query=${encodeURIComponent(serialized)}`);
    }
  }

  public navigateToCustomNavigation(webDokuId: string, customNodeId: string) {
    if (RBCheck.isNullOrEmpty(customNodeId)) {
      this.route(`${routeCustomNavigation}/${webDokuId}`);
    } else {
      this.route(`${routeCustomNavigation}/${webDokuId}`, { nodeId: customNodeId });
    }
  }

  public navigateToCustomTreeSearch(webDokuId: string, searchText: string) {
    if (RBCheck.isNullOrEmpty(searchText)) {
      this.route(`${routeCustomNavigation}/${webDokuId}`);
    } else {
      const options = new SearchOptions();
      options.webDokuId = webDokuId;
      options.isTokenSearch = false;
      options.addSearchText(searchText);
      const serialized = this.serializeSearchOptions(options);
      this.router.navigateByUrl(`${routeCustomNavigation}/${webDokuId}?query=${encodeURIComponent(serialized)}`);
    }
  }

  public navigateToQRScan() {
    this.route(routeScan);
  }

  public navigateToMessages() {
    this.route(routeMessages);
  }

  public navigateToWebDokuSelection() {
    this.route(routeWebDoku);
  }

  public navigateToSearch(searchText: string, tokenSearch = false) {
    this.navigateToDocumentSearch(this.webDokuService.currentWebDokuId, searchText, tokenSearch);
  }

  public navigateToDocumentSearch(webDokuId: string, searchText: string, tokenSearch = false) {
    const options = new SearchOptions();
    options.isTokenSearch = tokenSearch;
    options.addSearchText(searchText);
    const serialized = this.serializeSearchOptions(options);
    this.router.navigateByUrl(`${routeSearch}/${webDokuId}?query=${encodeURIComponent(serialized)}`);
  }

  public navigateToPasswordChange() {
    let destUrl = AppUrls.getAuthUrl(AppUrls.actionAuthPswChange);
    destUrl = `${destUrl}?ReturnUrl=${encodeURI(window.location.href)}`;
    window.location.href = destUrl;
  }

  getWebdokuParams(): any {
    if (this.webDokuService.currentWebDokuId == '') {
      return null;
    }
    return {
      wd: this.webDokuService.currentWebDokuId,
    };
  }

  getDocumentParams(docId: string): any {
    if (docId == null || this.webDokuService.currentWebDokuId == '') {
      // document and webdoku required
      return null;
    }
    return {
      wd: this.webDokuService.currentWebDokuId,
      docid: docId,
    };
  }

  getDocumentHistoryParams(docId: string, archived: string): any {
    if (docId == null || archived == null || this.webDokuService.currentWebDokuId == '') {
      // document and webdoku required
      return null;
    }
    return {
      wd: this.webDokuService.currentWebDokuId,
      docid: docId,
      archived: archived,
    };
  }

  public setQueryParams(key: string, value: string, replaceUrl = true) {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { [key]: value },
      skipLocationChange: false,
      queryParamsHandling: 'merge',
      replaceUrl,
    });
  }

  public getQueryParam(key: string): string | null {
    return this.activatedRoute.snapshot.queryParamMap.get(key);
  }

  public errorStop(errorId: string) {
    this.route(routeError, { error: errorId });
  }

  public createExternalUrl(tree: UrlTree) {
    return this.locationStrategy.prepareExternalUrl(this.router.serializeUrl(tree))
  }

  private route(path: string, params: any = null, clearRoutes = false) {
    console.log('route to ' + path, clearRoutes);
    if (params != null) {
      this.router
        .navigate([path, params], { replaceUrl: clearRoutes })
        .then(() => console.log(`navigation to ${path} complete`, params, clearRoutes))
        .catch((reason) => console.error(`navigation to ${path} failed`, params, reason));
    } else {
      this.router
        .navigate([path], { replaceUrl: clearRoutes })
        .then(() => console.log(`navigation to ${path} complete`))
        .catch((reason) => console.error(`navigagion problem to: ${path}`, reason));
    }

    if (clearRoutes) {
      // history.length;  ??
    }
  }

  public serializeSearchOptions(searchOptions: SearchOptions): string {
   return Base64.encode(JSON.stringify(searchOptions))
  }

  public deserializeSearchOptions(decodedSearchOptions: string): SearchOptions {
   return JSON.parse(Base64.decode(decodedSearchOptions))
  }
}
